import { useState } from "react";
import { supabase } from "../supabase";

// ── Send tailored email via Supabase Edge Function ────────────────────────────
async function sendEmail(type, data) {
  try {
    await supabase.functions.invoke("send-email", { body: { type, data } });
  } catch (err) {
    console.error("Email send error:", err);
  }
}


// ── Sample Data ───────────────────────────────────────────────────────────────
const sampleFriendlies = [
  {
    id: 1,
    club: "Riverside Rangers Youth",
    ageGroup: "U10",
    level: "Developing",
    surface: "3G Astro",
    region: "Hampshire",
    town: "Andover",
    date: "Sat 14 Dec",
    time: "10:00 AM",
    slots: 1,
    weather: true,
    notes: "3G pitch at Andover Sports Park — unaffected by weather. Looking for a similar standard team for a 7-a-side friendly.",
    contact: "manager@riversiderangers.com",
    posted: "Today",
    badge: "🟢",
    colour: "#10b981",
  },
  {
    id: 2,
    club: "Example FC Youth",
    ageGroup: "U12",
    level: "Competitive",
    surface: "Grass",
    region: "Hampshire",
    town: "Winchester",
    date: "Sun 15 Dec",
    time: "11:00 AM",
    slots: 1,
    weather: false,
    notes: "Looking for a pre-Christmas friendly on our grass pitch. Subject to weather — will confirm by Friday.",
    contact: "examplefc@gmail.com",
    posted: "2 days ago",
    badge: "🔵",
    colour: "#3b82f6",
  },
];

const AGE_GROUPS = ["All","U7","U8","U9","U10","U11","U12","U13","U14","U15","U16"];
const LEVELS     = ["All","Beginners Welcome","Developing","Competitive","Elite"];
const SURFACES   = ["All","Grass","3G Astro","4G Astro","Indoor","Any"];

const LEVEL_COLOURS = {
  "Beginners Welcome": { bg:"#d1fae5", text:"#065f46", dot:"#10b981" },
  "Developing":        { bg:"#dbeafe", text:"#1e40af", dot:"#3b82f6" },
  "Competitive":       { bg:"#fef3c7", text:"#92400e", dot:"#f59e0b" },
  "Elite":             { bg:"#fee2e2", text:"#991b1b", dot:"#ef4444" },
};

function LevelBadge({ level }) {
  const c = LEVEL_COLOURS[level] || { bg:"#f3f4f6", text:"#374151", dot:"#9ca3af" };
  return (
    <span style={{ background:c.bg, color:c.text, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, letterSpacing:0.5, display:"inline-flex", alignItems:"center", gap:5 }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:c.dot, display:"inline-block" }} />
      {level.toUpperCase()}
    </span>
  );
}

function SurfaceBadge({ surface, weatherProof }) {
  const colours = {
    "3G Astro": "#7c3aed",
    "4G Astro": "#7c3aed",
    "Indoor":   "#0369a1",
    "Grass":    "#15803d",
    "Any":      "#4b5563",
  };
  return (
    <span style={{ background:`${colours[surface]||"#374151"}22`, color:colours[surface]||"#9ca3af", border:`1px solid ${colours[surface]||"#374151"}44`, borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, display:"inline-flex", alignItems:"center", gap:5 }}>
      {surface === "Grass" ? "🌱" : surface === "Indoor" ? "🏟️" : "⚡"} {surface}
      {weatherProof && <span style={{ background:"#f59e0b22", color:"#f59e0b", borderRadius:20, padding:"1px 6px", fontSize:10, marginLeft:2 }}>☀️ All weather</span>}
    </span>
  );
}

// ── Friendly Card ─────────────────────────────────────────────────────────────
function FriendlyCard({ friendly, onOpen }) {
  return (
    <div onClick={() => onOpen(friendly)}
      style={{ background:"#111827", border:"1px solid #1f2937", borderRadius:14, padding:"20px 22px", cursor:"pointer", transition:"all 0.18s", position:"relative", overflow:"hidden" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor=friendly.colour; e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow=`0 8px 30px ${friendly.colour}30`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor="#1f2937"; e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}
    >
      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:friendly.colour, borderRadius:"14px 14px 0 0" }} />
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:26 }}>{friendly.badge}</span>
          <div>
            <div style={{ color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontSize:16, fontWeight:700, lineHeight:1.1 }}>{friendly.club}</div>
            <div style={{ color:"#6b7280", fontSize:11, marginTop:2 }}>{friendly.town}, {friendly.region}</div>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ background:"#1f2937", color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:18, borderRadius:8, padding:"3px 10px", letterSpacing:1 }}>{friendly.ageGroup}</div>
        </div>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <span style={{ color:"#10b981", fontSize:13 }}>📅</span>
        <span style={{ color:"#f3f4f6", fontSize:13, fontWeight:600 }}>{friendly.date}</span>
        <span style={{ color:"#4b5563", fontSize:12 }}>·</span>
        <span style={{ color:"#9ca3af", fontSize:12 }}>{friendly.time}</span>
      </div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
        <LevelBadge level={friendly.level} />
        <SurfaceBadge surface={friendly.surface} weatherProof={friendly.weather} />
      </div>
      <div style={{ color:"#6b7280", fontSize:12, lineHeight:1.5, marginBottom:10 }}>{friendly.notes.substring(0,90)}...</div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ color:"#4b5563", fontSize:11 }}>Posted {friendly.posted}</span>
        <span style={{ color:friendly.colour, fontSize:12, fontWeight:700, fontFamily:"'Oswald',sans-serif" }}>VIEW →</span>
      </div>
    </div>
  );
}

// ── Friendly Modal ────────────────────────────────────────────────────────────
function FriendlyModal({ friendly, onClose }) {
  if (!friendly) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"#000000cc", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#111827", border:`1px solid ${friendly.colour}`, borderRadius:18, padding:28, maxWidth:520, width:"100%", maxHeight:"90vh", overflowY:"auto", position:"relative" }}>
        <div style={{ position:"absolute", top:0, left:0, right:0, height:4, background:friendly.colour, borderRadius:"18px 18px 0 0" }} />
        <button onClick={onClose} style={{ position:"absolute", top:14, right:16, background:"#1f2937", border:"none", color:"#9ca3af", borderRadius:8, padding:"4px 10px", cursor:"pointer", fontSize:16 }}>✕</button>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:20 }}>
          <span style={{ fontSize:40 }}>{friendly.badge}</span>
          <div>
            <div style={{ color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontSize:22, fontWeight:700 }}>{friendly.club}</div>
            <div style={{ color:"#6b7280", fontSize:12 }}>{friendly.town}, {friendly.region}</div>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
          {[["Age Group",friendly.ageGroup],["Date",friendly.date],["Time",friendly.time],["Surface",friendly.surface]].map(([k,v]) => (
            <div key={k} style={{ background:"#1f2937", borderRadius:10, padding:"10px 14px" }}>
              <div style={{ color:"#4b5563", fontSize:10, marginBottom:2 }}>{k.toUpperCase()}</div>
              <div style={{ color:"#f3f4f6", fontSize:13, fontWeight:600 }}>{v}</div>
            </div>
          ))}
        </div>
        {friendly.weather && (
          <div style={{ background:"#f59e0b11", border:"1px solid #f59e0b33", borderRadius:10, padding:"10px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:18 }}>☀️</span>
            <span style={{ color:"#f59e0b", fontSize:12, fontWeight:700 }}>ALL WEATHER PITCH — Unaffected by rain, frost or snow</span>
          </div>
        )}
        <div style={{ marginBottom:16 }}>
          <div style={{ color:"#6b7280", fontSize:11, marginBottom:6 }}>ABILITY LEVEL</div>
          <LevelBadge level={friendly.level} />
        </div>
        <div style={{ marginBottom:18 }}>
          <div style={{ color:"#6b7280", fontSize:11, marginBottom:8 }}>DETAILS</div>
          <p style={{ color:"#d1d5db", fontSize:13, lineHeight:1.6 }}>{friendly.notes}</p>
        </div>
        <div style={{ background:"#1f2937", borderRadius:12, padding:"16px 18px", marginBottom:6 }}>
          <div style={{ color:"#6b7280", fontSize:11, marginBottom:8 }}>CONTACT THE MANAGER</div>
          <div style={{ color:"#f3f4f6", fontSize:13 }}>✉️ {friendly.contact}</div>
        </div>
        <button style={{ width:"100%", background:friendly.colour, color:"#fff", border:"none", borderRadius:10, padding:"13px 0", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:16, cursor:"pointer", marginTop:14, letterSpacing:1 }}>
          REQUEST THIS FRIENDLY →
        </button>
      </div>
    </div>
  );
}

// ── Post a Friendly Form ──────────────────────────────────────────────────────
function PostFriendlyForm({ onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ club:"", ageGroup:"U10", level:"Developing", surface:"Grass", date:"", time:"", region:"", town:"", weather:false, notes:"", contact:"" });
  const set = (k,v) => setForm(f => ({...f,[k]:v}));
  const inputStyle = { width:"100%", background:"#1f2937", border:"1px solid #374151", borderRadius:8, padding:"10px 12px", color:"#f3f4f6", fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };
  const labelStyle = { color:"#6b7280", fontSize:11, display:"block", marginBottom:5, letterSpacing:1 };

  if (step === 3) return (
    <div style={{ textAlign:"center", padding:20 }}>
      <div style={{ fontSize:56, marginBottom:12 }}>🤝</div>
      <div style={{ color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontSize:24, fontWeight:700, marginBottom:8 }}>Friendly Posted!</div>
      <div style={{ color:"#9ca3af", fontSize:14, marginBottom:20, lineHeight:1.6 }}>Your friendly request is now visible to clubs in your area. Expect to hear from managers soon!</div>
      <button onClick={onClose} style={{ background:"#10b981", color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:15, cursor:"pointer" }}>DONE</button>
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", gap:6, marginBottom:20 }}>
        {[1,2].map(s => <div key={s} style={{ flex:1, height:4, borderRadius:4, background:step>=s?"#10b981":"#1f2937" }} />)}
      </div>
      {step===1 && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div><label style={labelStyle}>CLUB NAME</label><input style={inputStyle} value={form.club} onChange={e=>set("club",e.target.value)} placeholder="e.g. Riverside Rangers Youth FC" /></div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div><label style={labelStyle}>AGE GROUP</label>
              <select style={inputStyle} value={form.ageGroup} onChange={e=>set("ageGroup",e.target.value)}>
                {AGE_GROUPS.filter(a=>a!=="All").map(a=><option key={a}>{a}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>LEVEL</label>
              <select style={inputStyle} value={form.level} onChange={e=>set("level",e.target.value)}>
                {LEVELS.filter(l=>l!=="All").map(l=><option key={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div><label style={labelStyle}>DATE</label><input style={inputStyle} type="date" value={form.date} onChange={e=>set("date",e.target.value)} /></div>
            <div><label style={labelStyle}>KICK OFF TIME</label><input style={inputStyle} type="time" value={form.time} onChange={e=>set("time",e.target.value)} /></div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <div><label style={labelStyle}>TOWN / CITY</label><input style={inputStyle} value={form.town} onChange={e=>set("town",e.target.value)} placeholder="e.g. Andover" /></div>
            <div><label style={labelStyle}>REGION / COUNTY</label><input style={inputStyle} value={form.region} onChange={e=>set("region",e.target.value)} placeholder="e.g. Hampshire" /></div>
          </div>
          <button onClick={()=>setStep(2)} style={{ background:"#10b981", color:"#fff", border:"none", borderRadius:10, padding:"12px 0", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:15, cursor:"pointer", letterSpacing:1 }}>NEXT →</button>
        </div>
      )}
      {step===2 && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div><label style={labelStyle}>PITCH SURFACE</label>
            <select style={inputStyle} value={form.surface} onChange={e=>set("surface",e.target.value)}>
              {SURFACES.filter(s=>s!=="All").map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ background:"#1f2937", borderRadius:10, padding:"12px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ color:"#f3f4f6", fontSize:13, fontWeight:600, marginBottom:2 }}>☀️ All weather pitch?</div>
              <div style={{ color:"#6b7280", fontSize:11 }}>Unaffected by rain, frost or snow</div>
            </div>
            <div onClick={()=>set("weather",!form.weather)} style={{ width:44, height:24, borderRadius:12, background:form.weather?"#10b981":"#374151", cursor:"pointer", position:"relative", transition:"background 0.2s" }}>
              <div style={{ position:"absolute", top:2, left:form.weather?20:2, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }} />
            </div>
          </div>
          <div><label style={labelStyle}>DETAILS / NOTES</label>
            <textarea style={{...inputStyle, resize:"vertical", minHeight:80}} value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="Tell other managers about the fixture — format, any specific requirements, parking etc." />
          </div>
          <div><label style={labelStyle}>CONTACT EMAIL</label><input style={inputStyle} value={form.contact} onChange={e=>set("contact",e.target.value)} placeholder="manager@yourclub.com" /></div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={()=>setStep(1)} style={{ flex:1, background:"#1f2937", color:"#9ca3af", border:"none", borderRadius:10, padding:"12px 0", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:14, cursor:"pointer" }}>← BACK</button>
            <button onClick={async()=>{
              const { error } = await supabase.from("friendlies").insert([{
                club: form.club, age_group: form.ageGroup, level: form.level,
                surface: form.surface, match_date: form.date, kick_off: form.time,
                region: form.region, town: form.town, weather_proof: form.weather,
                notes: form.notes, contact_email: form.contact, status: "active"
              }]);
              if (error) { alert("Error: " + error.message); } else {
                sendEmail("friendly", { club: form.club, age_group: form.ageGroup, match_date: form.date, town: form.town, weather_proof: form.weather, contact_email: form.contact });
                setStep(3);
              }
            }} style={{ flex:2, background:"#10b981", color:"#fff", border:"none", borderRadius:10, padding:"12px 0", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:15, cursor:"pointer", letterSpacing:1 }}>POST FRIENDLY</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Friendly Finder ──────────────────────────────────────────────────────
export default function FriendlyFinder() {
  const [selectedFriendly, setSelectedFriendly] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({ ageGroup:"All", level:"All", surface:"All", search:"" });
  const setFilter = (k,v) => setFilters(f => ({...f,[k]:v}));

  const filtered = sampleFriendlies.filter(f => {
    if (filters.ageGroup!=="All" && f.ageGroup!==filters.ageGroup) return false;
    if (filters.level!=="All" && f.level!==filters.level) return false;
    if (filters.surface!=="All" && f.surface!==filters.surface) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!f.club.toLowerCase().includes(q) && !f.town.toLowerCase().includes(q) && !f.region.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const btn = (active, colour) => ({
    background: active ? (colour||"#10b981") : "#1f2937",
    color: active ? "#fff" : "#9ca3af",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12,
    fontWeight:700, cursor:"pointer", fontFamily:"'Oswald',sans-serif",
    letterSpacing:0.5, transition:"all 0.15s",
  });

  return (
    <div style={{ fontFamily:"'Inter','Segoe UI',sans-serif" }}>

      {/* Hero */}
      <div style={{ background:"linear-gradient(135deg,#1a1a2e 0%,#16213e 60%,#0d1117 100%)", borderRadius:18, padding:"30px 28px", marginBottom:24, border:"1px solid #1f2937", position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", right:-20, top:-20, width:180, height:180, background:"#3b82f615", borderRadius:"50%", filter:"blur(40px)" }} />
        <div style={{ color:"#3b82f6", fontFamily:"'Oswald',sans-serif", fontSize:11, letterSpacing:3, marginBottom:8 }}>GRASSROOTING · FRIENDLY FINDER</div>
        <h2 style={{ color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontSize:28, fontWeight:700, margin:"0 0 8px", lineHeight:1.1 }}>Find a friendly.<br/>Fill the fixture gap.</h2>
        <p style={{ color:"#9ca3af", fontSize:13, margin:"0 0 20px", maxWidth:440, lineHeight:1.6 }}>Game called off? Need a pre-season fixture? Looking for a winter friendly on astro? Find the right opponent in minutes — filtered by age group, ability, location and pitch surface.</p>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <button onClick={()=>setShowForm(true)} style={{ background:"#3b82f6", color:"#fff", border:"none", borderRadius:10, padding:"11px 20px", fontFamily:"'Oswald',sans-serif", fontWeight:700, fontSize:13, cursor:"pointer", letterSpacing:1 }}>⚽ POST A FRIENDLY REQUEST</button>
        </div>
        {/* Weather callout */}
        <div style={{ marginTop:18, display:"flex", alignItems:"center", gap:10, background:"#f59e0b11", border:"1px solid #f59e0b33", borderRadius:10, padding:"10px 14px", maxWidth:440 }}>
          <span style={{ fontSize:20 }}>☔</span>
          <div style={{ color:"#f59e0b", fontSize:12, lineHeight:1.5 }}>
            <strong>Winter washout?</strong> Filter by 3G/4G Astro pitches to find all-weather friendlies unaffected by frost, rain or snow.
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ background:"#111827", border:"1px solid #1f2937", borderRadius:12, padding:"14px 16px", marginBottom:14 }}>
        <input value={filters.search} onChange={e=>setFilter("search",e.target.value)} style={{ width:"100%", background:"transparent", border:"none", color:"#f3f4f6", fontSize:15, outline:"none", boxSizing:"border-box" }} placeholder="🔍  Search by club, town or region..." />
      </div>

      {/* Filters */}
      <div style={{ marginBottom:20 }}>
        <div style={{ marginBottom:12 }}>
          <div style={{ color:"#4b5563", fontSize:10, letterSpacing:2, marginBottom:8 }}>AGE GROUP</div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {AGE_GROUPS.map(a => <button key={a} onClick={()=>setFilter("ageGroup",a)} style={btn(filters.ageGroup===a)}>{a}</button>)}
          </div>
        </div>
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          <div>
            <div style={{ color:"#4b5563", fontSize:10, letterSpacing:2, marginBottom:8 }}>PITCH SURFACE</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {SURFACES.map(s => <button key={s} onClick={()=>setFilter("surface",s)} style={btn(filters.surface===s)}>{s}</button>)}
            </div>
          </div>
          <div>
            <div style={{ color:"#4b5563", fontSize:10, letterSpacing:2, marginBottom:8 }}>LEVEL</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {LEVELS.map(l => <button key={l} onClick={()=>setFilter("level",l)} style={btn(filters.level===l)}>{l}</button>)}
            </div>
          </div>
        </div>
      </div>

      <div style={{ color:"#4b5563", fontSize:12, marginBottom:14 }}>{filtered.length} friendly request{filtered.length!==1?"s":""} available</div>

      {/* Cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
        {filtered.map(f => <FriendlyCard key={f.id} friendly={f} onOpen={setSelectedFriendly} />)}
      </div>

      {filtered.length===0 && (
        <div style={{ textAlign:"center", color:"#4b5563", padding:40, fontSize:14 }}>No friendlies match your filters — try broadening your search or post your own request!</div>
      )}

      {/* Post form modal */}
      {showForm && (
        <div style={{ position:"fixed", inset:0, background:"#000000dd", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }} onClick={()=>setShowForm(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:"#111827", border:"1px solid #1f2937", borderRadius:18, padding:28, maxWidth:500, width:"100%", maxHeight:"90vh", overflowY:"auto", position:"relative" }}>
            <button onClick={()=>setShowForm(false)} style={{ position:"absolute", top:14, right:16, background:"#1f2937", border:"none", color:"#9ca3af", borderRadius:8, padding:"4px 10px", cursor:"pointer", fontSize:16 }}>✕</button>
            <div style={{ color:"#f9fafb", fontFamily:"'Oswald',sans-serif", fontSize:20, fontWeight:700, marginBottom:20 }}>⚽ POST A FRIENDLY REQUEST</div>
            <PostFriendlyForm onClose={()=>setShowForm(false)} />
          </div>
        </div>
      )}

      {selectedFriendly && <FriendlyModal friendly={selectedFriendly} onClose={()=>setSelectedFriendly(null)} />}
    </div>
  );
}
